import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { MessageCircle, Phone, PhoneOff, Mic, MicOff, Lock, Shield, Send, CarFront, AlertCircle, Ban } from "lucide-react";
import useAnonymousCall from "../hooks/useAnonymousCall";
import OwnerCallWidget from "../components/call/OwnerCallWidget";
import { CallRequestChip, isCallRequestMsg } from "../components/call/CallRequestChip";
import { useI18n } from "../i18n";

/**
 * Owner-response page opened from the SMS/WhatsApp deep-link.
 * NO LOGIN REQUIRED — authentication is via the `access` query-param only.
 * Scope is strictly the single session — owner cannot navigate into dashboard.
 *
 * URL: /respond?session=<id>&access=<token>
 */
const API = "/api";

const useQuery = () => new URLSearchParams(useLocation().search);

const OwnerRespond = () => {
  const q = useQuery();
  const navigate = useNavigate();
  const { t, lang, setLangAccount } = useI18n();
  const sessionId = q.get("session");
  const accessToken = q.get("access");

  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState("");
  const [callActive, setCallActive] = useState(false);
  const msgsRef = useRef(null);

  const api = useCallback(async (method, path, data) => {
    const url = `${API}${path}${path.includes("?") ? "&" : "?"}access=${encodeURIComponent(accessToken || "")}`;
    const opts = { method, url, ...(data ? { data } : {}) };
    return axios(opts);
  }, [accessToken]);

  const reload = useCallback(async () => {
    try {
      const r = await api("get", `/public/owner-session/${sessionId}`);
      setSession(r.data.session);
      setMessages(r.data.messages || []);
      if (r.data.session?.owner_lang) setLangAccount(r.data.session.owner_lang);
      setFatalError(null);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det === "access_expired") setFatalError("expired");
      else if (det === "access_invalid" || det === "access_missing") setFatalError("invalid");
      else if (det === "session_not_found") setFatalError("not_found");
      else setFatalError("network");
    } finally {
      setLoading(false);
    }
  }, [api, sessionId, setLangAccount]);

  useEffect(() => {
    if (!sessionId || !accessToken) {
      setFatalError("invalid");
      setLoading(false);
      return;
    }
    reload();
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [sessionId, accessToken, reload]);

  useEffect(() => {
    msgsRef.current?.scrollTo({ top: msgsRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // WebRTC as owner — autostart only after acceptCall()
  const { phase: callPhase, muted, durationSec, error: callError, start: startCall, toggleMute, hangup: hangupCall, health } = useAnonymousCall({
    sessionId,
    role: "owner",
    access: accessToken,   // magic-link auth — no JWT needed
    autoStart: false,
  });

  const acceptCall = async () => {
    try {
      await api("put", `/public/owner-session/${sessionId}/call-status`, { status: "accepted" });
      setCallActive(true);
      startCall();
    } catch (e) {
      console.error(e);
    }
  };

  const declineCall = async () => {
    try {
      await api("put", `/public/owner-session/${sessionId}/call-status`, { status: "declined" });
      await reload();
    } catch (_) { /* ignore */ }
  };

  const endCall = () => {
    hangupCall();
    setCallActive(false);
  };

  const send = async () => {
    if (!text.trim() || replying) return;
    setReplying(true);
    try {
      await api("post", `/public/owner-session/${sessionId}/message`, { message: text.trim() });
      setText("");
      await reload();
    } catch (e) {
      console.error(e);
    } finally {
      setReplying(false);
    }
  };

  // iter59 fase 3.2: block the scanner's session (magic-link authenticated).
  const [blocking, setBlocking] = useState(false);
  const blockSession = async () => {
    if (blocking) return;
    if (session?.status === "blocked") return;
    const ok = window.confirm(t("owner_respond.block_confirm"));
    if (!ok) return;
    setBlocking(true);
    try {
      await api("post", `/public/owner-session/${sessionId}/block`, {});
      await reload();
    } catch (e) {
      console.error(e);
    } finally {
      setBlocking(false);
    }
  };

  // ----- Render -----

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-700 flex items-center justify-center">
        <div className="text-sm opacity-70">{t("owner_respond.loading")}</div>
      </div>
    );
  }

  if (fatalError) {
    const copy = {
      invalid:   { title: t("owner_respond.err_invalid_title"),  body: t("owner_respond.err_invalid_body") },
      expired:   { title: t("owner_respond.err_expired_title"),  body: t("owner_respond.err_expired_body") },
      not_found: { title: t("owner_respond.err_notfound_title"), body: t("owner_respond.err_notfound_body") },
      network:   { title: t("owner_respond.err_network_title"),  body: t("owner_respond.err_network_body") },
    }[fatalError];
    return (
      <div className="min-h-screen bg-slate-950 text-slate-700 flex items-center justify-center p-6">
        <div className="max-w-sm w-full rounded-2xl border border-slate-200 bg-white p-6 text-center space-y-3" data-testid="owner-respond-fatal">
          <AlertCircle className="w-10 h-10 mx-auto text-amber-400" />
          <h2 className="text-lg font-bold">{copy.title}</h2>
          <p className="text-sm text-slate-500">{copy.body}</p>
          <button
            onClick={() => navigate("/")}
            className="mt-3 w-full py-2.5 rounded-xl bg-slate-100 hover:bg-slate-100 text-sm"
            data-testid="owner-respond-home-btn"
          >
            {t("owner_respond.back_home")}
          </button>
        </div>
      </div>
    );
  }

  const isCallRequest = session?.is_call_request;
  const callStatus = session?.call_request_status;
  const scannerAcceptedView = isCallRequest && (callStatus === "accepted" || callActive);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#071b2f] via-[#0f3d5c] to-[#0c2a44] text-slate-700 flex flex-col"
      data-testid="owner-respond-page"
    >
      <header className="px-4 pt-5 pb-3 border-b border-slate-200 flex items-center gap-3">
        <button
          onClick={() => navigate("/")}
          className="shrink-0 w-9 h-9 rounded-xl bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600"
          aria-label={t("owner_respond.back_home")}
          data-testid="owner-respond-back-btn"
        >
          ←
        </button>
        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center">
          <CarFront className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-500">{t("owner_respond.header_sub")}</div>
          <div className="text-base font-bold truncate" data-testid="owner-respond-vehicle-label">
            {session?.vehicle_display_name || t("owner_respond.your_vehicle")}
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-700">
          <Lock className="w-3 h-3" />
          {t("owner_respond.anonymous")}
        </div>
      </header>

      {/* Incoming call banner */}
      {isCallRequest && callStatus === "pending" && !callActive && (
        <div
          className="m-4 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 flex flex-col gap-3"
          data-testid="owner-incoming-call"
        >
          <div className="flex items-center gap-2 text-emerald-700">
            <Phone className="w-5 h-5" /> <span className="font-bold">{t("owner_respond.incoming_call")}</span>
          </div>
          <p className="text-sm text-slate-600">
            {t("owner_respond.incoming_call_text")}
          </p>
          <div className="flex gap-2">
            <button
              onClick={acceptCall}
              className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
              data-testid="owner-respond-accept-call-btn"
            >
              {t("owner_respond.accept_call")}
            </button>
            <button
              onClick={declineCall}
              className="px-4 py-2.5 rounded-xl border border-white/15 hover:bg-slate-50 text-sm"
              data-testid="owner-respond-decline-call-btn"
            >
              {t("owner_respond.decline")}
            </button>
          </div>
        </div>
      )}

      {/* Active call widget — iter71: shared CallCore truth widget (no fake states) */}
      {scannerAcceptedView && (
        <OwnerCallWidget
          callPhase={callPhase}
          durationSec={durationSec}
          muted={muted}
          toggleMute={toggleMute}
          onHangup={endCall}
          onRetry={startCall}
          callError={callError}
          health={health}
        />
      )}

      {/* Messages */}
      <div ref={msgsRef} className="flex-1 px-4 py-3 space-y-2 overflow-auto" data-testid="owner-respond-messages">
        {messages.length === 0 && (
          <div className="text-center text-sm text-slate-500 pt-10">{t("owner_respond.no_messages")}</div>
        )}
        {/* iter71 2.3 — N intentos de llamada = UN chip, nunca N burbujas */}
        <CallRequestChip msgs={messages.filter(isCallRequestMsg)} />
        {messages.filter((m) => !isCallRequestMsg(m)).map((m) => {
          const mine = m.sender === "owner";
          return (
            <div
              key={m.id}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`owner-respond-msg-${m.sender}`}
            >
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                mine ? "bg-emerald-500 text-black rounded-br-sm" : "bg-slate-100 text-slate-700 rounded-bl-sm"
              }`}>
                <div className="text-[10px] opacity-70 mb-0.5">{mine ? t("owner_respond.you") : t("owner_respond.scanner_label")}</div>
                <div className="whitespace-pre-wrap break-words">{m.message}</div>
                <div className="text-[9px] opacity-50 mt-0.5 text-right">
                  {(() => { try { return new Date(m.created_at).toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply composer */}
      <div className="p-3 border-t border-slate-200 bg-black/30 flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 500))}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={session?.status === "blocked" ? t("owner_respond.composer_closed") : t("owner_respond.composer_ph")}
          className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400/60 disabled:opacity-50"
          disabled={replying || session?.status !== "active"}
          data-testid="owner-respond-input"
        />
        <button
          onClick={send}
          disabled={!text.trim() || replying || session?.status !== "active"}
          className="px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold disabled:opacity-40"
          data-testid="owner-respond-send-btn"
        >
          <Send className="w-4 h-4" />
        </button>
        <button
          onClick={blockSession}
          disabled={blocking || session?.status === "blocked"}
          title={t("owner_respond.block_title")}
          className="px-3 py-2.5 rounded-xl border border-red-400/30 text-red-700 hover:bg-red-500/10 disabled:opacity-40"
          data-testid="owner-respond-block-btn"
        >
          <Ban className="w-4 h-4" />
        </button>
      </div>

      {session?.status === "blocked" && (
        <div
          className="mx-3 mb-3 px-3 py-2 rounded-xl bg-red-500/10 border border-red-400/30 text-xs text-red-700 flex items-center gap-2"
          data-testid="owner-respond-blocked-banner"
        >
          <Ban className="w-3 h-3" /> {t("owner_respond.blocked_banner")}
        </div>
      )}

      <footer className="px-4 py-2 text-[10px] text-slate-500 text-center flex items-center justify-center gap-1">
        <Shield className="w-3 h-3" /> {t("owner_respond.footer_privacy")}
      </footer>
    </div>
  );
};

export default OwnerRespond;
